import React, { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { hashPassword } from '../utils/passwordUtils';
interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  firstLoginRequired?: boolean;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
  login: (userId: string, password: string) => Promise<boolean>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<boolean>;
  verifyOtp: (otp: string) => Promise<boolean>;
  resetPassword: (password: string, confirmPassword: string) => Promise<boolean>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [isOtpVerified, setIsOtpVerified] = useState<boolean>(false);
  // Initialize users in localStorage if not exists
  useEffect(() => {
    const storedUsers = localStorage.getItem('users');
    if (!storedUsers) {
      const initialUsers = [{
        id: 'user123',
        email: 'user@example.com',
        name: 'John Doe',
        role: 'admin',
        firstLoginRequired: true,
        password: hashPassword('Test@1234')
      }, {
        id: 'maker001',
        email: 'branchmaker@openremit.com',
        name: 'Branch Maker',
        role: 'maker',
        firstLoginRequired: true,
        password: hashPassword('Test@1234')
      }, {
        id: 'checker001',
        email: 'branchchecker@openremit.com',
        name: 'Branch Checker',
        role: 'checker',
        firstLoginRequired: true,
        password: hashPassword('Test@1234')
      }];
      localStorage.setItem('users', JSON.stringify(initialUsers));
    }
  }, []);
  // Check if user is already logged in
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    setIsAuthInitialized(true);
  }, []);
  const login = async (userId: string, password: string): Promise<boolean> => {
    // Get users from localStorage
    const storedUsers = localStorage.getItem('users');
    if (!storedUsers) return false;
    const users = JSON.parse(storedUsers);
    // Find user by username (userId) - ONLY check username, not branch code
    const foundUser = users.find((u: any) => {
      // Check if userId matches email or username
      const emailMatch = u.email === userId || u.email.split('@')[0] === userId;
      const nameMatch = u.name.toLowerCase().replace(/\s+/g, '') === userId.toLowerCase();
      return emailMatch || nameMatch;
    });
    if (!foundUser) return false;
    // Check password - ONLY password, branch code is metadata, not authentication
    const hashedInputPassword = hashPassword(password);
    if (foundUser.password !== hashedInputPassword) return false;
    // User authenticated successfully
    // Branch code will be set in LoginPage after successful authentication
    const authenticatedUser = {
      id: foundUser.id,
      email: foundUser.email,
      name: foundUser.name,
      role: foundUser.role,
      firstLoginRequired: foundUser.firstLoginRequired || false
    };
    setUser(authenticatedUser);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(authenticatedUser));
    return true;
  };
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
  };
  const requestPasswordReset = async (email: string): Promise<boolean> => {
    const storedUsers = localStorage.getItem('users');
    if (!storedUsers) return false;
    const users = JSON.parse(storedUsers);
    const userExists = users.some((user: any) => user.email === email);
    if (userExists) {
      setResetEmail(email);
      return true;
    }
    return false;
  };
  const verifyOtp = async (otp: string): Promise<boolean> => {
    if (otp === '123456') {
      setIsOtpVerified(true);
      return true;
    }
    return false;
  };
  const resetPassword = async (password: string, confirmPassword: string): Promise<boolean> => {
    if (password === confirmPassword && isOtpVerified) {
      setResetEmail(null);
      setIsOtpVerified(false);
      return true;
    }
    return false;
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    isAuthInitialized,
    login,
    logout,
    requestPasswordReset,
    verifyOtp,
    resetPassword
  }} data-id="element-2841">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};